import type {
  Column,
  ColumnKey,
  LegacyColumnArray,
  ColumnDefaults,
  Table,
  TableIndex,
  Relation,
  RelationType,
  Anchor,
  AnchorSide,
  Group,
  Groups,
} from '@/types'
import { genId } from './id'
import { newUid } from './uid'
import { deriveLegacyColumnKey, getColumnKeyFlags } from './columnKeys'
import { MIN_TABLE_WIDTH } from './geometry'

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
}

function readString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function readColumnKey(value: unknown): ColumnKey {
  return value === '' || value === 'PK' || value === 'FK' || value === 'UQ' ? value : ''
}

function readStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
}

function readFiniteNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

// ── SQL name helpers ──────────────────────────────────────────────────────────

function unquoteSqlIdentifier(value: string): string {
  if (!value) return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith('`') && trimmed.endsWith('`')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  ) {
    return trimmed.slice(1, -1).replace(/""/g, '"')
  }
  return trimmed
}

function splitQualifiedIdentifier(value: string): string[] {
  const text = String(value || '').trim()
  const parts: string[] = []
  let token = ''
  let quote: string | null = null

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (quote) {
      token += ch
      if (ch === quote) {
        if (quote === '"' && text[i + 1] === '"') {
          token += text[++i]
        } else {
          quote = null
        }
      }
      continue
    }
    if (ch === '"' || ch === '`') {
      quote = ch
      token += ch
      continue
    }
    if (ch === '.') {
      if (token.trim()) parts.push(unquoteSqlIdentifier(token))
      token = ''
      continue
    }
    token += ch
  }

  if (token.trim()) parts.push(unquoteSqlIdentifier(token))
  return parts.filter(Boolean)
}

export function resolveSqlName(value: string): { schema: string; name: string; fullName: string } {
  const parts = splitQualifiedIdentifier(value)
  const name = parts.length ? parts[parts.length - 1] : unquoteSqlIdentifier(value)
  const schema = parts.length > 1 ? parts.slice(0, -1).join('.') : ''
  const fullName = schema ? `${schema}.${name}` : name
  return { schema, name, fullName }
}

export function buildFullTableName(schema: string, name: string): string {
  const safeSchema = String(schema || '').trim()
  const safeName = String(name || '').trim()
  return safeSchema ? `${safeSchema}.${safeName}` : safeName
}

// ── Table display helpers ─────────────────────────────────────────────────────

export function getTableFullName(table: Pick<Table, 'fullName' | 'schema' | 'name' | 'id'>): string {
  if (!table) return ''
  return table.fullName || buildFullTableName(table.schema, table.name) || table.id || ''
}

export function getTableBaseName(table: Pick<Table, 'fullName' | 'name' | 'id'>): string {
  if (!table) return ''
  return table.name || resolveSqlName(table.fullName || table.id || '').name || table.id || ''
}

export function shouldDisplayFullTableNames(tables: Pick<Table, 'schema'>[] = []): boolean {
  const schemas = new Set(
    (tables || [])
      .map((tbl) => tbl?.schema || '')
      .filter(Boolean)
  )
  return schemas.size > 1
}

export function getTableDisplayName(
  table: Pick<Table, 'fullName' | 'schema' | 'name' | 'id'>,
  tables: Pick<Table, 'schema'>[] = []
): string {
  if (!table) return ''
  return shouldDisplayFullTableNames(tables)
    ? getTableFullName(table)
    : getTableBaseName(table)
}

// ── normCol ───────────────────────────────────────────────────────────────────
// Accepts both:
//   - legacy array: [key, name, type, notNull, blank, desc]
//   - new object:   { key, name, type, notNull, blank, desc }
// Feature 42: auto-migrates BOOL → BOOLEAN

export function normCol(c: unknown): Column {
  if (Array.isArray(c)) {
    const arr = c as LegacyColumnArray
    let type = readString(arr[2])
    if (type.toUpperCase() === 'BOOL') type = 'BOOLEAN'
    const key = readColumnKey(arr[0])
    const flags = getColumnKeyFlags({ key })
    return {
      id:      newUid(),
      key:     deriveLegacyColumnKey({ key, ...flags }),
      pk:      flags.pk,
      fk:      flags.fk,
      uq:      flags.uq,
      name:    readString(arr[1]),
      type,
      notNull: !!arr[3],
      blank:   arr[4] !== undefined ? !!arr[4] : true,
      desc:    readString(arr[5]),
      defaultValue: '',
      checkExpression: '',
    }
  }

  const obj = asRecord(c)
  let type = readString(obj.type)
  if (type.toUpperCase() === 'BOOL') type = 'BOOLEAN'
  const key = readColumnKey(obj.key)
  const flags = getColumnKeyFlags({
    key,
    pk: obj.pk === undefined ? undefined : !!obj.pk,
    fk: obj.fk === undefined ? undefined : !!obj.fk,
    uq: obj.uq === undefined ? undefined : !!obj.uq,
  })
  return {
    id:      readString(obj.id) || newUid(),
    key:     deriveLegacyColumnKey({ key, ...flags }),
    pk:      flags.pk,
    fk:      flags.fk,
    uq:      flags.uq,
    name:    readString(obj.name),
    type,
    notNull: !!obj.notNull,
    blank:   obj.blank !== undefined ? !!obj.blank : true,
    desc:    readString(obj.desc),
    defaultValue: readString(obj.defaultValue),
    checkExpression: readString(obj.checkExpression),
  }
}

// ── normIdx ───────────────────────────────────────────────────────────────────

export function normIdx(ix: unknown): TableIndex {
  const obj = asRecord(ix)
  return {
    id:     readString(obj.id, genId('idx')),
    name:   readString(obj.name),
    cols:   readStringArray(obj.cols),
    unique: !!obj.unique,
    type:   readString(obj.type, 'btree'),
  }
}

// ── normTable ─────────────────────────────────────────────────────────────────
// Accepts tables from:
//   - new store format (Column objects)
//   - .morpherd legacy (Column objects or arrays)
//   - localStorage legacy (Column arrays)

export function normTable(t: unknown): Table {
  const obj = asRecord(t)
  const schema = readString(obj.schema)
  const resolvedName = resolveSqlName(readString(obj.fullName) || readString(obj.id)).name
  const name = readString(obj.name, resolvedName || readString(obj.id))
  const fullName = readString(obj.fullName, buildFullTableName(schema, name) || readString(obj.id))

  return {
    // Identidade = UUID. NÃO derivar de fullName (acoplamento antigo). A migração
    // de dados legados (id=fullName) é feita por migrateDiagramIdentity antes daqui.
    id:       readString(obj.id) || newUid(),
    schema,
    name,
    fullName,
    comment:  readString(obj.comment),
    g:        readString(obj.g),
    x:        readFiniteNumber(obj.x),
    y:        readFiniteNumber(obj.y),
    width:    obj.width === undefined ? undefined : Math.max(MIN_TABLE_WIDTH, readFiniteNumber(obj.width)),
    cols:     (Array.isArray(obj.cols) ? obj.cols : []).map(normCol),
    indexes:  (Array.isArray(obj.indexes) ? obj.indexes : []).map(normIdx),
  }
}

// ── normRel ───────────────────────────────────────────────────────────────────
// Accepts both:
//   - legacy array: [from, col, to, type, mode?, mid?]
//   - object format: { id, from, col, to, type, ... }

export function normRel(r: unknown): Relation {
  if (Array.isArray(r)) {
    const arr = r as [string, string, string, string, unknown?, number?]
    return {
      id:            genId('rel'),
      from:          arr[0] || '',
      col:           arr[1] || '',
      to:            arr[2] || '',
      type:          (arr[3] as RelationType) || '1-N',
      fromCardinality: undefined,
      toCardinality: undefined,
      mid:           arr[5] != null ? arr[5] : null,
      pts:           [],
      fromAnchor:    null,
      toAnchor:      null,
      fromTableName: '',
      toTableName:   '',
      fromSchema:    '',
      toSchema:      '',
      fromColumn:    arr[1] || '',
      toColumn:      'id',
    }
  }

  const obj = asRecord(r)

  const fromAnchor = isValidAnchor(obj.fromAnchor) ? obj.fromAnchor : null
  const toAnchor = isValidAnchor(obj.toAnchor) ? obj.toAnchor : null

  return {
    id:            readString(obj.id, genId('rel')),
    from:          readString(obj.from),
    col:           readString(obj.col),
    to:            readString(obj.to),
    type:          (obj.type as RelationType) || '1-N',
    fromCardinality: ['one', 'many', 'zero-one', 'zero-many'].includes(String(obj.fromCardinality))
      ? obj.fromCardinality as Relation['fromCardinality']
      : undefined,
    toCardinality: ['one', 'many', 'zero-one', 'zero-many'].includes(String(obj.toCardinality))
      ? obj.toCardinality as Relation['toCardinality']
      : undefined,
    constraintName: readString(obj.constraintName),
    relationshipKey: readString(obj.relationshipKey),
    mid:           obj.mid != null && Number.isFinite(Number(obj.mid)) ? Number(obj.mid) : null,
    pts:           Array.isArray(obj.pts) ? obj.pts.filter((point): point is number => typeof point === 'number') : [],
    fromAnchor,
    toAnchor,
    fromTableName: readString(obj.fromTableName),
    toTableName:   readString(obj.toTableName),
    fromSchema:    readString(obj.fromSchema),
    toSchema:      readString(obj.toSchema),
    fromColumn:    readString(obj.fromColumn, readString(obj.col)),
    toColumn:      readString(obj.toColumn, 'id'),
    waypoints: Array.isArray(obj.waypoints)
      ? (obj.waypoints as unknown[]).filter(isValidPoint).map(p => ({ x: p.x, y: p.y }))
      : undefined,
    isManual: typeof obj.isManual === 'boolean' ? obj.isManual : undefined,
    labelT:        typeof obj.labelT        === 'number' ? obj.labelT        : undefined,
  }
}

function isValidAnchor(a: unknown): a is Anchor {
  if (!a || typeof a !== 'object') return false
  const obj = a as Record<string, unknown>
  const validSides: AnchorSide[] = ['top', 'bottom', 'left', 'right']
  return validSides.includes(obj.side as AnchorSide) && typeof obj.offsetPercent === 'number'
}

function isValidPoint(p: unknown): p is { x: number; y: number } {
  if (!p || typeof p !== 'object') return false
  const obj = p as Record<string, unknown>
  return typeof obj.x === 'number' && typeof obj.y === 'number'
}

// ── normGroup ─────────────────────────────────────────────────────────────────
// Accepts new format { id, name, color } and legacy { id, l, c }

export function normGroup(g: unknown): Group {
  const obj = asRecord(g)
  return {
    id:    readString(obj.id, genId('grp')),
    name:  readString(obj.name, readString(obj.l)),
    color: readString(obj.color, readString(obj.c, '#6366F1')),
  }
}

// ── normGroups ────────────────────────────────────────────────────────────────
// Accepts both Record<string, Group> and array of Groups

export function normGroups(input: unknown): Groups {
  if (!input || typeof input !== 'object') return {}
  if (Array.isArray(input)) {
    return Object.fromEntries(
      input.map((g) => {
        const norm = normGroup(g)
        return [norm.id, norm]
      })
    )
  }
  const raw = input as Record<string, unknown>
  return Object.fromEntries(
    Object.entries(raw).map(([k, v]) => {
      const norm = normGroup({ id: k, ...(v as object) })
      return [k, norm]
    })
  )
}

// ── Initial state constants ───────────────────────────────────────────────────

export const INIT_GROUPS: Groups = {}

export const INIT_DEFAULTS: ColumnDefaults = {
  prefix: [
    normCol({ key: 'PK', name: 'id',  type: 'BIGINT',      notNull: true,  blank: false, desc: 'Identificador único' }),
  ],
  suffix: [
    normCol({ key: '',   name: 'created_at',  type: 'TIMESTAMPTZ', notNull: true,  blank: false, desc: '' }),
    normCol({ key: '',   name: 'modified_at', type: 'TIMESTAMPTZ', notNull: true,  blank: false, desc: '' }),
    normCol({ key: '',   name: 'is_active',   type: 'BOOLEAN',     notNull: true,  blank: false, desc: 'Soft delete' }),
  ],
}

export const INIT_TABLES: Table[] = []
export const INIT_RELS:   Relation[] = []
